import React from 'react'

import { Link } from '@amzn/stencil-react-components/link'
import { Span } from '@amzn/stencil-react-components/text'

type FormattedContent = React.ReactNode

// Helper function to convert URLs to clickable links
export const makeLinksClickable = (text: string): FormattedContent => {
    const urlRegex = /(https?:\/\/[^\s]+)/g
    const parts = text.split(urlRegex)

    return parts.map((part, index) => {
        if (urlRegex.test(part)) {
            return (
                <Link key={index} href={part} target='_blank'>
                    {part}
                </Link>
            )
        }
        return part
    })
}

const formatStringValue = (value: string): FormattedContent => {
    if (!value.includes('\n')) {
        return <Span>{makeLinksClickable(value)}</Span>
    }

    const lines = value.split('\n')
    return (
        <div style={{ marginTop: '8px', paddingLeft: '16px' }}>
            {lines.map((line, i) => {
                const bulletMatch = line.trim().match(/^[-*]\s+(.*)$/)
                if (bulletMatch) {
                    return (
                        <div key={i} style={{ marginBottom: '4px', display: 'flex' }}>
                            <Span>• </Span>
                            <Span>{makeLinksClickable(bulletMatch[1])}</Span>
                        </div>
                    )
                }
                return line ? <div key={i}>{makeLinksClickable(line)}</div> : null
            })}
        </div>
    )
}

const formatArrayValue = (value: unknown[]): FormattedContent => (
    <div style={{ paddingLeft: 'S300' }}>
        {value.map((item, i) => (
            <div key={i} style={{ marginBottom: '4px' }}>
                <Span>•</Span>
                <Span>{typeof item === 'object' ? JSON.stringify(item) : String(item)}</Span>
            </div>
        ))}
    </div>
)

const formatObjectValue = (value: object): FormattedContent => (
    <div style={{ paddingLeft: 'S300' }}>{JSON.stringify(value, null, 2)}</div>
)

const formatJsonValue = (value: unknown): FormattedContent => {
    if (typeof value === 'string') return formatStringValue(value)
    if (Array.isArray(value)) return formatArrayValue(value)
    if (typeof value === 'object' && value !== null) return formatObjectValue(value)
    return <Span>{String(value)}</Span>
}

const isJsonLike = (text: string): boolean => {
    const trimmed = text.trim()
    return trimmed.startsWith('{') && trimmed.endsWith('}')
}

export const tryFormatJson = (text: string): React.ReactNode | null => {
    if (!isJsonLike(text)) return null

    try {
        const jsonObj: Record<string, unknown> = JSON.parse(text)

        return (
            <div style={{ marginTop: '8px' }}>
                {Object.entries(jsonObj).map(([key, value], index) => {
                    const formattedKey = (
                        <Span key={`key-${index}`} fontWeight='bold'>
                            {key
                                .split('_')
                                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')}
                            :
                        </Span>
                    )

                    return (
                        <div key={index} style={{ marginBottom: '12px' }}>
                            {formattedKey} {formatJsonValue(value)}
                        </div>
                    )
                })}
            </div>
        )
    } catch (e) {
        return null
    }
}
